import { useTranslation } from "react-i18next";
import { useFieldContext } from "./Form";
import { faX, type IconDefinition } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { CharacterCount } from "@pulse/components/CharacterCount";
import { getFirstErrorMessage } from "@pulse/utils/formErrors";
import type { DetailedHTMLProps, InputHTMLAttributes, TextareaHTMLAttributes } from "react";
import { Button, Checkbox } from "frontend-components";

function FieldErrorMessage({ errors }: { errors: readonly unknown[] | undefined }) {
    const { t } = useTranslation();
    const errorMessage = getFirstErrorMessage(errors);

    if (!errorMessage) {
        return null;
    }

    return <div className="text-chameleon-pink text-sm">{t(errorMessage)}</div>;
}

function MaxLengthHelperMessage({ show }: { show: boolean }) {
    const { t } = useTranslation();

    if (!show) {
        return null;
    }

    return <div className="text-sm">{t("common.maxLengthReached")}</div>;
}

function hasReachedMaxLength(value: string, maxCharacters?: number) {
    return maxCharacters !== undefined && value.length >= maxCharacters;
}

export function Input({
    maxCharacters,
    className,
    ...props
}: {
    maxCharacters?: number;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>) {
    const field = useFieldContext();
    const value = String(field.state.value ?? "");
    const isAtMaxLength = hasReachedMaxLength(value, maxCharacters);
    const hasFieldError = field.state.meta.errors.length > 0;
    const hasErrorStyle = hasFieldError || isAtMaxLength;

    return (
        <label className="w-full flex flex-col gap-1">
            {maxCharacters && (
                <CharacterCount currentLength={value.length} maxLength={maxCharacters} />
            )}
            <input
                className={`self-stretch rounded-none border bg-white px-3 py-2.5 ${
                    hasErrorStyle
                        ? "border-chameleon-pink"
                        : "border-border-input hover:border-border-input-hover focus:border-border-focus"
                } ${className ?? ""}`.trim()}
                {...props}
                maxLength={maxCharacters ?? props.maxLength}
                onChange={(ev) => field.handleChange(ev.target.value)}
                onBlur={field.handleBlur}
                value={value}
            />
            <FieldErrorMessage errors={field.state.meta.errors} />
            <MaxLengthHelperMessage show={!hasFieldError && isAtMaxLength} />
        </label>
    );
}

export function MultipleChoiceInput({
    icon,
    removeFn,
    canRemove = true,
    maxCharacters,
    className,
    ...props
}: {
    icon?: IconDefinition;
    removeFn: () => void;
    canRemove?: boolean;
    maxCharacters?: number;
} & DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>) {
    const field = useFieldContext();
    const value = String(field.state.value ?? "");
    const isAtMaxLength = hasReachedMaxLength(value, maxCharacters);
    const hasFieldError = field.state.meta.errors.length > 0;
    const hasErrorStyle = hasFieldError || isAtMaxLength;
    const { t } = useTranslation();

    return (
        <label className="flex w-full min-w-0 flex-col gap-1">
            {maxCharacters && (
                <div className="self-end mr-11">
                    <CharacterCount currentLength={value.length} maxLength={maxCharacters} />
                </div>
            )}
            <div className="flex w-full min-w-0 flex-col gap-1">
                <div className="flex w-full min-w-0 items-center gap-2">
                    {icon && <FontAwesomeIcon icon={icon} className="shrink-0" />}
                    <input
                        className={`h-8 min-w-0 flex-1 rounded-none border px-3 ${
                            hasErrorStyle
                                ? "border-chameleon-pink"
                                : "border-border-input hover:border-border-input-hover focus:border-border-focus"
                        } ${className ?? ""}`.trim()}
                        {...props}
                        maxLength={maxCharacters ?? props.maxLength}
                        onChange={(ev) => field.handleChange(ev.target.value)}
                        onBlur={field.handleBlur}
                        value={value}
                    />
                    <Button
                        type="button"
                        variant="ghost"
                        className="hover:text-compendia-foreground/60 p-0 px-2"
                        aria-label={t("common.remove")}
                        aria-disabled={!canRemove}
                        onClick={removeFn}
                    >
                        <FontAwesomeIcon icon={faX} />
                    </Button>
                </div>
            </div>
            <FieldErrorMessage errors={field.state.meta.errors} />
            <MaxLengthHelperMessage show={!hasFieldError && isAtMaxLength} />
        </label>
    );
}

export function CommentSectionCheckbox() {
    const field = useFieldContext();
    const { t } = useTranslation();
    return (
        <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
                checked={Boolean(field.state.value)}
                onCheckedChange={(checked) => field.handleChange(checked)}
                onBlur={field.handleBlur}
            />
            <span className="text-sm">{t("question.allowComments")}</span>
        </label>
    );
}

export function RequiredCheckbox() {
    const field = useFieldContext();
    const { t } = useTranslation();
    return (
        <label className="flex cursor-pointer items-center gap-2">
            <Checkbox
                checked={!field.state.value}
                onCheckedChange={(checked) => field.handleChange(!checked)}
                onBlur={field.handleBlur}
            />
            <span className="text-sm">{t("question.requiredQuestion")}</span>
        </label>
    );
}

export function TextArea({
    maxCharacters,
    className,
    ...props
}: {
    maxCharacters?: number;
} & DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>) {
    const field = useFieldContext();
    const value = String(field.state.value ?? "");
    const isAtMaxLength = hasReachedMaxLength(value, maxCharacters);
    const hasFieldError = field.state.meta.errors.length > 0;
    const hasErrorStyle = hasFieldError || isAtMaxLength;

    return (
        <label className="w-full flex flex-col gap-1">
            {maxCharacters && (
                <CharacterCount currentLength={value.length} maxLength={maxCharacters} />
            )}
            <textarea
                {...props}
                className={`w-full rounded-none border bg-white px-3 py-2 ${
                    hasErrorStyle
                        ? "border-chameleon-pink"
                        : "border-border-input hover:border-border-input-hover focus:border-border-focus"
                } ${className ?? ""}`.trim()}
                maxLength={maxCharacters ?? props.maxLength}
                onChange={(ev) => field.handleChange(ev.target.value)}
                onBlur={field.handleBlur}
                value={value}
            />
            <FieldErrorMessage errors={field.state.meta.errors} />
            <MaxLengthHelperMessage show={!hasFieldError && isAtMaxLength} />
        </label>
    );
}
